import React from 'react'
import { Fixed } from '../../parts/bases/Fixed'
import { VStack } from '../../parts/bases/VStack'
import { Text } from '../../parts/bases/Text'
import { HStack } from '../../parts/bases/HStack'
import { Center } from '../../parts/bases/Center'
import { CollectibleItemCategory, IconType } from './query.generated'
import { Clickable } from '../../parts/bases/Clickable'
import { Absolute } from '../../parts/bases/Absolute'
import Image from 'next/image'
import { PinkNeonButton } from '../../parts/buttons/PinkNeonButton'
import { Spinner } from '../../parts/commons/Spinner'
import config from '@/tailwind.config'

type TabType = 'Icon' | 'Frame'

type Icon = {
  type: IconType
  id: string
  name: string
  imageUrl: string
  category: CollectibleItemCategory | null
  subCategory: string | null
}

type Frame = {
  id: string
  name: string
  imageUrl: string
  category: CollectibleItemCategory | null
  subCategory: string | null
}

export type Props = {
  state: 'Default' | 'Loading'
  selectTab: TabType
  onClickTab: (tab: TabType) => void
  onClickCancel: () => void
  onClickSave: () => void

  inWorldIconList: Array<Icon>
  myPfpIconList: Array<Icon>
  selectIcon: Icon | null
  savedIcon: Icon | null
  onClickIcon: (selectIcon: Icon) => void
  onClickPfpOfficialSite: () => void

  frameList: Array<Frame>
  selectFrame: Frame | null
  savedFrame: Frame | null
  onClickFrame: (selectFrame: Frame) => void
}

export const Layout = (props: Props) => {
  return (
    <Fixed className="top-0 left-0 right-0 bottom-0 z-[100] h-full w-full items-center justify-center bg-dialogBg">
      <div className="flex h-full w-full items-center justify-center">
        <VStack
          className="relative h-[700px] w-[1044px] items-center rounded-xl border-2 border-neon-blue bg-black py-8 px-20"
          onClick={(event) => event.stopPropagation()}
        >
          <Text className="text-[28px] font-bold text-white">Change Icon</Text>
          <HStack className="mt-6 w-full items-center justify-around px-60">
            <TabItem tabType="Icon" selectTab={props.selectTab} onClick={() => props.onClickTab('Icon')} />
            <TabItem tabType="Frame" selectTab={props.selectTab} onClick={() => props.onClickTab('Frame')} />
          </HStack>

          {props.selectTab === 'Icon' ? <IconTab {...props} /> : <FrameTab {...props} />}

          <HStack className="mt-6 gap-x-6">
            <PinkNeonButton type="Sub" onClick={props.onClickCancel}>
              Cancel
            </PinkNeonButton>
            <PinkNeonButton
              onClick={props.onClickSave}
              disabled={props.selectIcon === props.savedIcon && props.selectFrame === props.savedFrame}
            >
              Save
            </PinkNeonButton>
          </HStack>
        </VStack>
      </div>

      {props.state === 'Loading' && (
        <Absolute className="top-0 z-[100] h-screen w-screen flex-1 bg-black/60">
          <Center className="h-full w-full">
            <Spinner size={30} visible color={config.theme.extend.colors.neon.blue} />
          </Center>
        </Absolute>
      )}
    </Fixed>
  )
}

const TabItem = (props: { tabType: TabType; selectTab: TabType; onClick: (tab: TabType) => void }) => {
  const { tabType, selectTab, onClick } = props
  return (
    <VStack className={`cursor-pointer items-center gap-y-2.5 text-center`} onClick={() => onClick('Icon')}>
      <Text
        className={`w-20 border-b-neon-blue text-center text-xl text-neon-blue ${
          tabType === selectTab ? 'border-b' : 'border-b-0'
        }`}
      >
        {tabType}
      </Text>
    </VStack>
  )
}

const IconTab = (props: Props) => {
  return (
    <VStack className="gray_scroll_bar mt-4 h-[450px] w-full items-start overflow-x-hidden">
      <Text className="text-2xl font-bold text-white">In-World</Text>
      <div className="mb-1 grid grid-cols-5 gap-6 pr-4">
        {props.inWorldIconList.map((item) => (
          <Icon
            key={item.id}
            icon={item}
            isSelected={item.id === props.selectIcon?.id}
            isSaved={item.id === props.savedIcon?.id}
            onClickIcon={props.onClickIcon}
          />
        ))}
      </div>

      <Text className="mt-6 mb-1 text-2xl font-bold text-white">My PFPs</Text>
      {props.myPfpIconList.length > 0 ? (
        <div className="grid grid-cols-5 gap-6 pr-4">
          {props.myPfpIconList.map((item) => (
            <Icon
              key={item.id}
              icon={item}
              isSelected={item.id === props.selectIcon?.id}
              isSaved={item.id === props.savedIcon?.id}
              onClickIcon={props.onClickIcon}
            />
          ))}
        </div>
      ) : (
        <VStack className="w-full items-center justify-center">
          <Text
            className="mt-6 whitespace-pre-wrap text-center text-sm text-white"
            style={{ textShadow: '0px 0px 8px #805BE7' }}
          >
            {props.inWorldIconList.length !== 1
              ? 'Upgrade your icon with an official PFP!'
              : 'Icons can be obtained at events or by purchasing PFPs!'}
          </Text>
          <Center className="mt-4 mb-16">
            <PinkNeonButton onClick={props.onClickPfpOfficialSite}>
              <Text className="text-xl font-bold text-white">PFP Official Site</Text>
            </PinkNeonButton>
          </Center>
        </VStack>
      )}
    </VStack>
  )
}

const FrameTab = (props: Props) => {
  return (
    <VStack className="gray_scroll_bar mt-4 h-[450px] items-start overflow-x-hidden pr-4">
      <div className="grid grid-cols-5 gap-6">
        {props.frameList.map((item) => (
          <Frame
            key={item.id}
            frame={item}
            isSelected={item.id === props.selectFrame?.id}
            isSaved={item.id === props.savedFrame?.id}
            onClickFrame={props.onClickFrame}
          />
        ))}
      </div>
    </VStack>
  )
}

const Icon = (props: {
  icon: Icon
  isSelected: boolean
  isSaved: boolean
  onClickIcon: (selectIcon: Icon) => void
}) => (
  <VStack className="items-center">
    <Center
      className={`overflow-hidden rounded-lg border-2 bg-black ${
        props.isSelected ? 'border-[#FCFF54]' : 'border-[#52369F]'
      }`}
    >
      <Clickable onClick={() => props.onClickIcon(props.icon)}>
        <Center>
          {props.icon.imageUrl && (
            <Image width={160} height={160} objectFit="contain" alt="user icon" src={props.icon.imageUrl} />
          )}
        </Center>
        {props.isSaved && (
          <Absolute className="bottom-2 mx-auto w-full">
            <Center className="mx-auto">
              <Text className="rounded-[10px] bg-akiverse-pink/70 px-2 py-[1px] text-center text-xs font-bold text-white">
                Settings
              </Text>
            </Center>
          </Absolute>
        )}
      </Clickable>
    </Center>
    <Text className="text-base font-bold text-white">{props.icon.name}</Text>
  </VStack>
)

const Frame = (props: {
  frame: Frame
  isSelected: boolean
  isSaved: boolean
  onClickFrame: (selectFrame: Frame) => void
}) => (
  <VStack className="items-center">
    <Center
      className={`overflow-hidden rounded-lg bg-black ${
        props.isSelected ? 'border-2 border-[#FCFF54]' : 'p-0.5'
      }`}
    >
      <Clickable onClick={() => props.onClickFrame(props.frame)}>
        <Center>
          {props.frame.imageUrl && (
            <Image width={160} height={160} objectFit="contain" alt="user icon" src={props.frame.imageUrl} />
          )}
        </Center>
        {props.isSaved && (
          <Absolute className="bottom-2 mx-auto w-full">
            <Center className="mx-auto">
              <Text className="rounded-[10px] bg-akiverse-pink/70 px-2 py-[1px] text-center text-xs font-bold text-white">
                Settings
              </Text>
            </Center>
          </Absolute>
        )}
      </Clickable>
    </Center>
    <Text className="text-base font-bold text-white">{props.frame.name}</Text>
  </VStack>
)
